import { type CommentThreadJSON } from '@databases/model/commentThreads';
import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { createField, getExistingDatabase } from '@databases/utils/database/utils';

import { databasesToContentId } from './constants';

const selectOptions = [
  {
    color: 'tealLight',
    id: '1',
    label: 'Small'
  },
  {
    color: 'yellowLight',
    id: '2',
    label: 'Medium'
  },
  {
    color: 'greenLight',
    id: '3',
    label: 'Large'
  },
  {
    color: 'tealLight',
    id: '4',
    label: 'Extra Small'
  }
];

const getValueForType = (
  fieldType: DatabaseFieldType,
  entryIndex: number,
  fieldIndex: number
) => {
  switch (fieldType) {
    case DatabaseFieldType.TEXT:
      return { plain: `${entryIndex}-${fieldIndex}` };
    case DatabaseFieldType.SELECT:
      return [selectOptions[entryIndex].id];
    case DatabaseFieldType.JIRA_ISSUE: {
      const jiraValue = [];
      for (let i = 0; i < entryIndex; i++) {
        jiraValue[i] = {
          key: `test_${entryIndex}`,
          id: `${entryIndex}_${fieldIndex}`
        };
      }
      return jiraValue;
    }
    case DatabaseFieldType.ATTACHMENT:
      return []; // TODO CTD-4394: Replace with valid image
  }
};

const generateEntriesById = (
  baseDatabase: Database<ObjectMode.CREATE>,
  fields: DatabaseField[]
) => {
  const result: Record<string, DatabaseEntry> = {};

  Object.values(baseDatabase.entriesById).forEach((entry, entryIndex) => {
    result[entry.id] = {
      id: entry.id,
      values: Object.fromEntries(
        fields.map((field, fieldIndex) => [
          field.id,
          {
            type: field.type,
            value: getValueForType(field.type, entryIndex, fieldIndex)
          }
        ])
      ) as Record<string, DatabaseValue<DatabaseFieldType, object>>
    };
  });

  return result;
};

const generateComments = (
  baseDatabase: Database<ObjectMode.EXISTING>,
  fieldIds: string[]
) => {
  const commentThreads: Record<string, [CommentThreadJSON]> = {};

  const entries = baseDatabase.entries;
  commentThreads[`${entries[0]}-${fieldIds[28]}`] = [
    {
      threadId: 'thread-1',
      isResolved: false,
      commentCount: 1,
      versionNumber: 0
    }
  ];
  commentThreads[`${entries[0]}-${fieldIds[29]}`] = [
    {
      threadId: 'thread-2',
      isResolved: false,
      commentCount: 3,
      versionNumber: 0
    }
  ];
  commentThreads[`${entries[1]}-${fieldIds[3]}`] = [
    {
      threadId: 'resolved-thread',
      isResolved: true,
      commentCount: 3,
      versionNumber: 0
    }
  ];
  commentThreads[`${entries[2]}-${fieldIds[0]}`] = [
    {
      threadId: 'thread-3',
      isResolved: false,
      commentCount: 1,
      versionNumber: 0
    }
  ];

  return commentThreads;
};

export const getDatabaseWithJiraIssueFieldsAndComments =
  (): Database<ObjectMode.EXISTING> => {
    const baseSelectField = createField(DatabaseFieldType.SELECT);

    const fields = [
      ...Array.from({ length: 30 }, (_, i) => ({
        ...createField(DatabaseFieldType.TEXT),
        name: `Text Field${i === 0 ? '' : ' ' + i}`
      })),
      {
        ...createField(DatabaseFieldType.ATTACHMENT),
        name: `Attachment Field`
      },
      {
        ...baseSelectField,
        name: `Tag Field`,
        settings: {
          ...baseSelectField.settings,
          multi: false,
          selectOptions: [...selectOptions]
        }
      },
      {
        ...createField(DatabaseFieldType.JIRA_ISSUE),
        name: `Jira Issue Field`,
        cloudId: '1234'
      },
      {
        ...createField(DatabaseFieldType.JIRA_ISSUE),
        name: `Jira Issue Field 2`,
        cloudId: '1235'
      }
    ];

    const baseDatabase = getExistingDatabase({
      contentId: databasesToContentId['databaseWithJiraIssueFieldsAndComments'],
      baseFields: fields
    });

    const entriesById = generateEntriesById(baseDatabase, fields);
    const fieldIds = fields.map((field) => field.id);

    const database = {
      ...baseDatabase,
      fields: fieldIds,
      fieldsById: Object.fromEntries(fields.map((field) => [field.id, field])),
      entriesById,
      entries: Object.keys(entriesById)
    };

    return {
      ...database,
      commentThreads: generateComments(baseDatabase, fieldIds)
    };
  };
